import React, { FC } from 'react'
import { MapContainer, MapContainerProps, TileLayer } from 'react-leaflet'
import tw from 'tailwind-styled-components'
import styled from 'styled-components'
import { renderToStaticMarkup } from 'react-dom/server'
import { theme } from '../../config'
import { FullScreenControl } from './Controls/FullScreenControl'
import { ZoomControl } from './Controls/ZoomControl'
import { TileLayerControl, tileURLs } from './Controls/TileLayerControl'
import { SvgIcon } from '../SvgIcon'
import NoBoundary from '@foros-fe/assets/icons/no-boundary.svg'
import ExpandIcon from '@foros-fe/assets/icons/expand.svg'
import ShrinkIcon from '@foros-fe/assets/icons/shrink.svg'

import 'leaflet.fullscreen'
import 'leaflet/dist/leaflet.css'
import 'leaflet.fullscreen/Control.FullScreen.css'

type Props = MapContainerProps & {
  staticMap?: boolean
  fallback?: boolean
}

const expandIconUrl =
  'data:image/svg+xml,' +
  encodeURIComponent(renderToStaticMarkup(<ExpandIcon width={18} height={18} />))
const shrinkIconUrl =
  'data:image/svg+xml,' +
  encodeURIComponent(renderToStaticMarkup(<ShrinkIcon width={18} height={18} />))

const FluidMap = styled(MapContainer)`
  && {
    width: 100%;
    height: 100%;
    font-family: ${theme.fontFamily.sans.join(', ')};
  }

  a {
    color: ${theme.colors.primary[500]};
  }

  .leaflet-top,
  .leaflet-bottom {
    z-index: 400;

    &.leaflet-left {
      bottom: 0;

      .leaflet-control {
        position: static;
      }
    }
  }

  && .leaflet-bar {
    border-width: 1px;
    border-color: ${theme.colors.gray[400]};
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06),
      0px 0px 1px rgba(0, 0, 0, 0.04);

    a {
      width: 38px;
      height: 38px;
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .leaflet-popup-content-wrapper {
    border-radius: 4px;
  }

  .leaflet-popup-content p {
    margin: 0px;
  }

  .leaflet-marker-icon.cluster {
    background-color: ${theme.colors.secondary[300]};
    border-radius: 20px;
    background-clip: padding-box;
  }

  && .fullscreen-icon {
    background-size: auto;
    background-image: url(${expandIconUrl});
    background-position: center;

    &.leaflet-fullscreen-on {
      background-image: url(${shrinkIconUrl});
      background-position: center;
    }
  }
`
const Container = tw.div`w-full h-full flex`
const FallbackContainer = tw.div`flex flex-1 justify-center items-center bg-gray-400`

const staticOptions = {
  doubleClickZoom: false,
  closePopupOnClick: false,
  dragging: false,
  zoomSnap: 0,
  zoomDelta: 0,
  trackResize: false,
  touchZoom: false,
  scrollWheelZoom: false,
}

const Map: FC<Props> = ({ children, staticMap, fallback, ...props }) => {
  return (
    <Container>
      {fallback ? (
        <FallbackContainer>
          <SvgIcon src={NoBoundary} size={78} />
        </FallbackContainer>
      ) : (
        <FluidMap
          minZoom={6}
          maxZoom={18}
          attributionControl={false}
          zoomControl={false}
          {...(staticMap && staticOptions)}
          {...props}
        >
          {staticMap ? (
            <TileLayer url={tileURLs.orthographic} />
          ) : (
            <>
              <TileLayerControl />
              <ZoomControl />
              <FullScreenControl />
            </>
          )}
          {children}
        </FluidMap>
      )}
    </Container>
  )
}

export default Map
